import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CalendarDays, Clock, MapPin, CheckCircle2, X, Users, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { guestGetString } from '../lib/guestStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch (_) { return iso; }
};

const GuestEventPage = () => {
  const { shareCode } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Prefill from prior guest-request/OTP flow — the venue already verified
  // this phone number in a previous session, so there's no reason to make
  // the guest type it again for an event RSVP.
  const [name, setName] = useState(() => guestGetString('guestName') || '');
  const [phone, setPhone] = useState(() => guestGetString('guestPhone') || '');
  const [partySize, setPartySize] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(null);
  const [posterFullscreen, setPosterFullscreen] = useState(false);
  const prefilled = Boolean(guestGetString('guestName') && guestGetString('guestPhone'));

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events/public/${shareCode}`);
      setEvent(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Event not found');
    } finally {
      setLoading(false);
    }
  }, [shareCode]);

  useEffect(() => { load(); }, [load]);

  const register = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!phone.trim()) { toast.error('Phone is required'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/events/public/${shareCode}/register`, {
        guest_name: name.trim(),
        guest_phone: phone.trim(),
        party_size: partySize,
      });
      setRegistered({
        ...r.data.registration,
        duplicate: !!r.data.duplicate,
      });
      if (r.data.duplicate) {
        toast.info('Already registered — see you there!');
      } else {
        toast.success('You\'re in! See you at the event.');
      }
    } catch (e) {
      toast.error('Registration failed', { description: e?.response?.data?.detail });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-gray-500">Loading event…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-8 text-center">
        <CalendarDays className="w-12 h-12 text-gray-700 mb-4" />
        <p className="text-white font-bold text-lg mb-2">{error}</p>
        <p className="text-gray-500 text-sm">The event may have ended or the link is no longer active.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-12" data-testid="guest-event-page">
      {event.poster_url && (
        <button
          type="button"
          onClick={() => setPosterFullscreen(true)}
          className="relative w-full aspect-video sm:aspect-[16/8] overflow-hidden block group focus:outline-none"
          data-testid="event-poster-trigger"
          aria-label="View poster full screen"
        >
          <img
            src={event.poster_url}
            alt={event.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black pointer-events-none" />
          <span className="absolute bottom-3 right-3 text-[11px] font-bold uppercase tracking-wider bg-black/60 backdrop-blur px-2.5 py-1 rounded-full text-white/90 group-hover:bg-black/80">
            Tap to expand
          </span>
        </button>
      )}
      <div className={`max-w-xl mx-auto px-6 relative ${event.poster_url ? '-mt-12' : 'pt-10'}`}>
        <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-xl">
          <h1 className="text-3xl lg:text-4xl font-extrabold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
            {event.name}
          </h1>
          {event.venue_name && (
            <p className="text-gray-400 text-sm mb-4 flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-[#FF007F]" /> {event.venue_name}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-sm mb-6">
            <p className="text-gray-300 flex items-center gap-1.5">
              <CalendarDays className="w-4 h-4 text-[#00FFFF]" /> {formatDate(event.event_date)}
            </p>
            {(!event.is_all_day && event.start_time) || event.is_all_day ? (
              <p className="text-gray-300 flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-[#00FFFF]" />
                {event.is_all_day ? 'All day' : `${event.start_time}${event.end_time ? ` – ${event.end_time}` : ''}`}
              </p>
            ) : null}
          </div>
          {event.description && (
            <p className="text-gray-300 text-sm whitespace-pre-line mb-6 leading-relaxed">{event.description}</p>
          )}

          {registered ? (
            <div
              className="bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-2xl p-6 text-center"
              data-testid="event-register-success"
            >
              <CheckCircle2 className="w-12 h-12 text-[#39FF14] mx-auto mb-3" />
              <p className="text-white font-bold text-lg mb-1">
                {registered.duplicate ? "You're already on the list" : "You're registered!"}
              </p>
              <p className="text-gray-300 text-sm">
                Hey {registered.guest_name}, see you on {formatDate(event.event_date)}.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {prefilled && (
                <div
                  className="rounded-lg border border-[#00F5FF]/30 bg-[#00F5FF]/5 px-3 py-2 flex items-center gap-2"
                  data-testid="event-register-prefilled-hint"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#00F5FF] flex-shrink-0" />
                  <p className="text-[#7ff1ff] text-xs">
                    We remembered you — hit Register to confirm.
                  </p>
                </div>
              )}
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider">Your name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Aman Sharma"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="event-register-name"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider">Phone</Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9876543210"
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="event-register-phone"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Number of guests (you + your group)
                </Label>
                <div className="mt-1.5 flex items-center gap-3 bg-black/40 border border-white/10 rounded-md px-2 py-1.5" data-testid="event-register-party-stepper">
                  <button
                    type="button"
                    onClick={() => setPartySize((n) => Math.max(1, n - 1))}
                    disabled={partySize <= 1}
                    className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30 flex items-center justify-center text-white transition-colors"
                    data-testid="event-register-party-minus"
                    aria-label="Decrease party size"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span
                    className="flex-1 text-center text-white font-bold text-lg tabular-nums"
                    data-testid="event-register-party-value"
                  >
                    {partySize}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPartySize((n) => Math.min(20, n + 1))}
                    disabled={partySize >= 20}
                    className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30 flex items-center justify-center text-white transition-colors"
                    data-testid="event-register-party-plus"
                    aria-label="Increase party size"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-gray-500 text-[11px] mt-1">
                  We&apos;ll ask the venue to block a table for your group.
                </p>
              </div>
              <Button
                onClick={register}
                disabled={submitting}
                className="bg-[#FF007F] hover:bg-[#cc0066] text-white font-bold tracking-wider rounded-full w-full py-6 text-base mt-2"
                data-testid="event-register-submit"
              >
                {submitting ? 'REGISTERING…' : (partySize > 1 ? `REGISTER · ${partySize} GUESTS` : 'REGISTER')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Full-screen poster overlay — tap anywhere to dismiss */}
      {posterFullscreen && event.poster_url && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setPosterFullscreen(false)}
          data-testid="event-poster-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label="Event poster"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPosterFullscreen(false); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            data-testid="event-poster-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={event.poster_url}
            alt={event.name}
            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GuestEventPage;
